import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import Card from './components/Card';
import Button from './components/Button';
import Input from './components/Input';
import Badge from './components/Badge';
import Tab from './components/Tab';
import { Trash2, Info, Settings, FileSearch, Users } from 'lucide-react';
import useReferencePages from './hooks/useReferencePages';
import Loader from './components/Loader';
import { useToast } from './ToastContext';
import { updateProject, deleteProject } from './api';
import ProjectMembers from './ProjectMembers';

export default function ProjectEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { project } = useOutletContext();
  const { showToast } = useToast();

  const [form, setForm] = useState({
    name: '',
    url: '',
    favicon_url: '',
    description: '',
  });

  const [faviconPreview, setFaviconPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Charger les vraies données du projet
  useEffect(() => {
    if (project) {
      // Extraire le nom du projet depuis l'URL si le nom n'est pas défini
      let projectName = project.name;
      if (!projectName && project.url) {
        try {
          const url = new URL(project.url);
          projectName = url.hostname.replace('www.', '');
        } catch (e) {
          projectName = project.url;
        }
      }

      setForm({
        name: projectName || 'Mon projet',
        url: project.url || '',
        favicon_url: project.favicon_url || '',
        description: project.description || '',
      });
      setFaviconPreview(project.favicon_url || '');
    }
  }, [project]);

  // Gestion des changements de formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Gestion de l'upload de favicon
  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Note : l'upload local n'est pas persisté. Utilisez une URL externe dans le champ ci-dessus.
    const url = URL.createObjectURL(file);
    setFaviconPreview(url);
    // Ne pas mettre à jour form.favicon_url avec une URL temporaire
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    const projectData = {
      name: form.name.trim(),
      url: form.url.trim(),
      favicon_url: form.favicon_url.trim() || null,
      description: form.description.trim() || null,
    };

    try {
      const updatedProject = await updateProject(slug, projectData);

      setSuccess(true);
      showToast('Projet mis à jour avec succès !', 'success');

    } catch (e) {
      console.error('Erreur lors de la mise à jour:', e);
      const errorMessage = e.response?.data?.detail || e.message || 'Erreur lors de la mise à jour';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Suppression du projet (la confirmation est gérée par le modal)
  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteProject(slug);
      showToast('Projet supprimé avec succès !', 'success');
      setTimeout(() => {
        navigate('/projects');
      }, 500);
    } catch (e) {
      console.error('Erreur lors de la suppression:', e);
      const errorMessage = e.response?.data?.detail || e.message || 'Erreur lors de la suppression';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
      setShowDelete(false);
    }
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader className="scale-90" label="Chargement du projet..." />
      </div>
    );
  }

  const {
    sitemapUrls, selectedReferencePages, loading: referencePagesLoading,
    saved: referencePagesSaved, data: referencePagesData,
    activeTab: referencePagesTab, setActiveTab: setReferencePagesTab,
    togglePage: handleReferencePageToggle, saveReferencePages: handleSaveReferencePages, getSortedUrls
  } = useReferencePages(project, null);

  return (
    <div>

      <Tab
        active={activeTab}
        onChange={setActiveTab}
        className="mb-6"
        tabData={[
          { key: 'general', label: 'Paramètres généraux', icon: <Settings size={18} /> },
          { key: 'reference', label: 'Pages de référence', icon: <FileSearch size={18} /> },
          { key: 'team', label: 'Équipe', icon: <Users size={18} /> },
        ]}
      />

      {activeTab === 'general' && (
        <Card className="p-8">
          <div className="flex items-center justify-between gap-4 mb-6">
            <h1 className="font-heading text-2xl font-bold text-blue-gray-900">Paramètres du projet</h1>
            <button
              type="button"
              onClick={() => setShowDelete(true)}
              className="p-2 rounded-full bg-transparent border border-red-200 text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 transition"
              title="Supprimer le projet"
            >
              <Trash2 size={24} className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* Contenu du formulaire */}
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-blue-gray-700 font-semibold mb-1">Nom du projet</label>
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Nom du projet"
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-blue-gray-700 font-semibold mb-1">URL du site</label>
                  <Input
                    name="url"
                    value={form.url}
                    onChange={handleChange}
                    placeholder="https://www.monsite.com"
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-blue-gray-700 font-semibold mb-1">Favicon</label>
                  <div className="flex items-center gap-4">
                    <Input
                      name="favicon_url"
                      value={form.favicon_url}
                      onChange={handleChange}
                      placeholder="URL du favicon"
                      className="w-full"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFaviconUpload}
                      className="hidden"
                      id="favicon-upload"
                    />
                    <label
                      htmlFor="favicon-upload"
                      className="cursor-pointer px-3 py-1 bg-blue-gray-50 rounded-lg border border-blue-100 text-xs hover:bg-blue-100"
                    >
                      Upload
                    </label>
                    {faviconPreview && (
                      <img
                        src={faviconPreview}
                        alt="favicon preview"
                        className="w-10 h-10 rounded bg-white border border-blue-100 shadow-sm"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-blue-gray-700 font-semibold mb-1">Description</label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Description du projet"
                    className="w-full rounded-lg border border-blue-100 bg-blue-gray-50 p-3 min-h-[80px] text-sm resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Boutons de soumission */}
            <div className="flex gap-3 pt-4 border-t border-neutral-200">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sauvegarde...
                  </>
                ) : (
                  'Sauvegarder les modifications'
                )}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(`/projects/${slug}`)}
              >
                Annuler
              </Button>
            </div>

            {/* Messages de feedback */}
            {success && (
              <div className="p-3 bg-green-100 text-green-700 border border-green-200 rounded-lg text-sm">
                ✅ Projet mis à jour avec succès !
              </div>
            )}
            {error && (
              <div className="p-3 bg-red-100 text-red-700 border border-red-200 rounded-lg text-sm">
                ❌ {error}
              </div>
            )}
          </form>
        </Card>
      )}

      {/* Pages de référence */}
      {activeTab === 'reference' && (
        <Card className="p-8 mt-6">
          <div className="flex items-center justify-between mb-4 border-b border-neutral-100 pb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-xl font-bold text-blue-gray-900">Pages de référence</h2>
              </div>
              <p className="text-sm text-blue-gray-500">Sélectionnez les pages qui serviront de référence pour les analyses</p>
            </div>
            {referencePagesSaved && <Badge color="success">Enregistrées</Badge>}
          </div>

          <div className="flex gap-2 mb-4 border-b border-neutral-100 pb-2">
            {[['selection', `Sélection (${selectedReferencePages?.length || 0})`], ['utilisees', 'Détails des pages']].map(([k, l]) => (
              <button key={k} onClick={() => setReferencePagesTab(k)}
                className={`text-sm pb-2 border-b-2 transition-colors ${referencePagesTab === k ? 'font-semibold text-accent border-accent' : 'text-neutral-500 border-transparent hover:text-neutral-700'}`}>{l}</button>
            ))}
          </div>

          {referencePagesTab === 'selection' ? (
            <div className="border border-neutral-200 rounded-lg p-2 bg-neutral-50 max-h-64 overflow-y-auto">
              {getSortedUrls().map(url => (
                <label key={url} className="flex items-center gap-3 p-2 rounded-md hover:bg-white cursor-pointer transition-colors">
                  <input type="checkbox" className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent" checked={selectedReferencePages?.includes(url)} onChange={() => handleReferencePageToggle(url)} />
                  <span className="text-sm text-neutral-700 truncate flex-1">{url}</span>
                </label>
              ))}
              {sitemapUrls.length === 0 && (
                <div className="p-4 text-center text-sm text-neutral-500">Aucune URL trouvée dans le sitemap.</div>
              )}
              <div className="mt-4 text-right px-2 sticky bottom-0 bg-neutral-50/90 backdrop-blur-sm py-2">
                <Button size="sm" onClick={handleSaveReferencePages} disabled={referencePagesLoading}>
                  {referencePagesLoading ? 'Enregistrement...' : 'Enregistrer la sélection'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
              {selectedReferencePages?.map(url => {
                const p = referencePagesData?.find(d => d.url === url);
                return (
                  <div key={url} className="p-3 bg-neutral-50 rounded-lg text-sm border border-neutral-100">
                    <div className="font-medium text-neutral-800 break-all">{p?.title || url}</div>
                    {p?.meta_description && <div className="text-neutral-500 mt-1">{p.meta_description}</div>}
                  </div>
                );
              })}
              {selectedReferencePages?.length === 0 && (
                <div className="p-4 text-center text-sm text-neutral-500">Aucune page sélectionnée.</div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Onglet Équipe */}
      {activeTab === 'team' && (
        <ProjectMembers project={project} />
      )}

      {/* Modal de suppression */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full !bg-white shadow-xl border-blue-gray-200">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-blue-gray-900 mb-2">
                Supprimer le projet
              </h3>
              <p className="text-blue-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible et supprimera toutes les données associées.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="error"
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Suppression...' : 'Supprimer'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowDelete(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

    </div>
  );
}
